import type { NlpFilters } from '@massilia/queries'

import type {
  LuggageFilterValue,
  StopsFilterValue,
} from '@/lib/schemas/flight-results-search.schema'

import type { SegmentTimeFilter } from './time-filter'

export interface FlightUrlFilterParams {
  filterAirlines?: string
  filterDurationMax?: number
  filterDurationMin?: number
  filterLuggage?: LuggageFilterValue
  filterPriceMax?: number
  filterPriceMin?: number
  filterStops?: StopsFilterValue
  filterTimeRanges?: string
}

// Patch passed to setFilters() to clear all sidebar filters at once. Centralized
// here so the sidebar "Réinitialiser" button and the empty-state card stay in
// sync — adding a new filter field requires touching only this object.
export const RESET_FILTERS_PATCH: Partial<FlightUrlFilterParams> = {
  filterAirlines: undefined,
  filterDurationMax: undefined,
  filterDurationMin: undefined,
  filterLuggage: undefined,
  filterPriceMax: undefined,
  filterPriceMin: undefined,
  filterStops: undefined,
  filterTimeRanges: undefined,
}

export function nlpFiltersToUrlParams(filters: NlpFilters): FlightUrlFilterParams {
  const out: FlightUrlFilterParams = {}
  if (filters.stops && filters.stops !== 'all') out.filterStops = filters.stops
  if (filters.luggage) out.filterLuggage = filters.luggage
  if (typeof filters.maxPrice === 'number' && Number.isFinite(filters.maxPrice)) {
    out.filterPriceMax = filters.maxPrice
  }
  if (
    typeof filters.maxDurationMinutes === 'number' &&
    Number.isFinite(filters.maxDurationMinutes)
  ) {
    out.filterDurationMax = filters.maxDurationMinutes
  }
  if (filters.excludedAirlines && filters.excludedAirlines.length > 0) {
    out.filterAirlines = filters.excludedAirlines.join(',')
  }
  if (filters.timeFilters && filters.timeFilters.length > 0) {
    out.filterTimeRanges = JSON.stringify(filters.timeFilters)
  }
  return out
}

// Normalize the JSON time-filter param coming from the URL. The chat layer may
// emit entries with only departureTimeSlots OR only arrivalTimeSlots (cf.
// NlpSegmentTimeFilter where both are optional); downstream consumers expect
// both arrays to be present, so default the missing one to []. Also tolerates
// malformed JSON and entries without a numeric segmentIndex.
export function parseFilterTimeRanges(raw: string | undefined): SegmentTimeFilter[] {
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry): SegmentTimeFilter[] => {
    if (!entry || typeof entry !== 'object') return []
    const e = entry as Partial<SegmentTimeFilter>
    if (typeof e.segmentIndex !== 'number') return []
    return [
      {
        arrivalTimeSlots: Array.isArray(e.arrivalTimeSlots) ? e.arrivalTimeSlots : [],
        departureTimeSlots: Array.isArray(e.departureTimeSlots) ? e.departureTimeSlots : [],
        segmentIndex: e.segmentIndex,
      },
    ]
  })
}

export function urlParamsToNlpFilters(params: FlightUrlFilterParams): NlpFilters {
  const out: NlpFilters = {}
  if (params.filterStops && params.filterStops !== 'all') out.stops = params.filterStops
  if (params.filterLuggage) out.luggage = params.filterLuggage
  if (typeof params.filterPriceMax === 'number') out.maxPrice = params.filterPriceMax
  if (typeof params.filterDurationMax === 'number') {
    out.maxDurationMinutes = params.filterDurationMax
  }
  if (params.filterAirlines) {
    const codes = params.filterAirlines.split(',').filter(Boolean)
    if (codes.length > 0) out.excludedAirlines = codes
  }
  const timeFilters = parseFilterTimeRanges(params.filterTimeRanges)
  if (timeFilters.length > 0) out.timeFilters = timeFilters
  return out
}
